import {
  createContext,
  type ReactNode,
  useCallback,
  useState,
  useRef,
  useMemo,
  useEffect,
  useReducer,
  useContext,
  type MutableRefObject,
} from 'react'
import { useDispatch } from 'react-redux'
import {
  type CartItem,
  type Option,
  type OrderInfo,
  type Product,
  type ProductList,
  type Range,
} from '../../types/products/productTypes'
// @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
import { Address, type PastVisit, type Pharmacy } from '../../types/types'
import { type OrderCategory, type PharmacyPartnerType, PharmacyPartners, Step } from './common'
import { type StepData, useSteps } from './hooks/useSteps'
import { useIsMoudDeliveryAvailable } from './hooks/useIsMoudDeliveryAvailable'
import { getPastVisitsForProduct } from 'store/products/productsThunk'
import { getDeliveryType } from './utils'
import { useGetOrderNameFromProduct } from './hooks/useGetOrderNameFromProduct'
// @ts-expect-error TS7016 -- MIGRATION: not approved, fix when encountered
import { v4 as uuidv4 } from 'uuid'
import { useAutoUpdatingRef } from '@quickmd-llc/frontend-utils'

export type DeliveryType = 'home' | 'pharmacy'
export type HomeDeliveryType = 'honeybee' | 'thrive'

// Local reducer to manage core state transitions atomically
// Note: keep Provider; reducer only organizes updates.

type ReducerState = {
  deliveryType: DeliveryType
  category: OrderCategory | undefined
  orderInfo: OrderInfo | undefined
  cartItems: CartItem[]
}

type Action =
  | { type: 'SET_DELIVERY_TYPE'; payload: { deliveryType: DeliveryType } }
  | { type: 'SET_CATEGORY'; payload: { category: OrderCategory | undefined } }
  | { type: 'SET_ORDER_INFO'; payload: { orderInfo: OrderInfo | undefined } }
  | { type: 'SET_CART_ITEMS'; payload: { items: CartItem[] } }
  | { type: 'ADD_CART_ITEM'; payload: { item: CartItem } }
  | { type: 'REMOVE_CART_ITEM'; payload: { cartId: string } }

const initialReducerState: ReducerState = {
  deliveryType: 'pharmacy',
  category: undefined,
  orderInfo: undefined,
  cartItems: [],
}

function productOrderReducer(state: ReducerState, action: Action): ReducerState {
  switch (action.type) {
    case 'SET_DELIVERY_TYPE': {
      const { deliveryType } = action.payload
      // Clear Address when delivery type changes to avoid stale addresses
      const nextOrderInfo = state.orderInfo ? { ...state.orderInfo, Address: undefined } : state.orderInfo
      // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
      return { ...state, deliveryType, orderInfo: nextOrderInfo }
    }
    case 'SET_CATEGORY': {
      return { ...state, category: action.payload.category }
    }
    case 'SET_ORDER_INFO': {
      return { ...state, orderInfo: action.payload.orderInfo }
    }
    case 'SET_CART_ITEMS': {
      return { ...state, cartItems: action.payload.items }
    }
    case 'ADD_CART_ITEM': {
      return { ...state, cartItems: [...state.cartItems, action.payload.item] }
    }
    case 'REMOVE_CART_ITEM': {
      return { ...state, cartItems: state.cartItems.filter((x) => x.CartId !== action.payload.cartId) }
    }
    default:
      return state
  }
}

export type ProductOrderContextType = {
  category: OrderCategory | undefined
  cartItems: CartItem[]
  deliveryType: DeliveryType | undefined
  orderInfo: OrderInfo | undefined
  isPatientRefillAllowed: boolean
  note: string | undefined
  patientId: string
  pharmacy: Pharmacy | undefined
  refillsQuantity: number | undefined
  resetRefillsQuantity: () => void
  selectedOption: Option | undefined
  editCartItemId: string
  getCartSubTotal: () => number
  setCategory: StateSetter<OrderCategory | undefined>
  setCartItems: StateSetter<CartItem[]>
  setEditCartItemId: StateSetter<string>
  setDeliveryType: StateSetter<DeliveryType>
  setNote: StateSetter<string>
  setOrderInfo: StateSetter<OrderInfo>
  setPharmacy: StateSetter<Pharmacy | undefined>
  setSelectedOption: StateSetter<Option | undefined>
  setRefillsQuantity: StateSetter<number | undefined>
  productList: ProductList[] | undefined
  currentStepId: Step
  product: Product | undefined
  loading: boolean
  isShowing: boolean
  showOrderFailed: boolean
  showOrderCreated: boolean
  showOrderCreatedTreat: boolean
  showDiscardOrdersWarning: string
  error: string
  noteError: string
  setProductList: StateSetter<ProductList[] | undefined>
  setProduct: StateSetter<Product | undefined>
  setLoading: StateSetter<boolean>
  setIsShowing: StateSetter<boolean>
  setShowOrderFailed: StateSetter<boolean>
  setShowDiscardOrdersWarning: StateSetter<string>
  setShowOrderCreated: StateSetter<boolean>
  setShowOrderCreatedTreat: StateSetter<boolean>
  setError: StateSetter<string>
  setNoteError: StateSetter<string>
  isMoudDeliveryAvailable: boolean
  isPharmacyHub: boolean
  previousCartStep: MutableRefObject<Step | undefined>
  isMoudHomeDelivery: boolean
  isMoudPharmacyPickup: boolean
  isMHTPharmacyPickup: boolean
  isMHTHomeDelivery: boolean
  isPharmacyPickup: boolean
  isMHT: boolean
  visitList: PastVisit[]
  setVisitList: StateSetter<PastVisit[]>
  homeDeliveryPriceRange: Range
  setHomeDeliveryPriceRange: StateSetter<Range>
  thriveProducts: Product[]
  standardProducts: Product[]
  openProductsAction: (args: {
    value: boolean
    categoryArg?: OrderCategory
    list?: ProductList[]
    range?: Range
    pharmacyFromProps?: Pharmacy
  }) => void
  getPriceRange: (products: Product[]) => Range
  setPharmacyPartnerType: StateSetter<PharmacyPartnerType>
  pharmacyPartnerType: string
  updateCart: (currentOrderInfo?: OrderInfo, currentOption?: Option, currentProduct?: Product) => void
} & StepData

const ProductOrderContext = createContext<ProductOrderContextType | undefined>(undefined)

type Props = {
  children: ReactNode
  isPatientRefillAllowed?: boolean
  patientId: string
  visitId: string
}

function ProductOrderProvider({ children, isPatientRefillAllowed = false, patientId, visitId }: Props) {
  const previousCartStep = useRef<Step | undefined>()
  const reduxDispatch = useDispatch()
  const [state, dispatch] = useReducer(productOrderReducer, initialReducerState)
  const { deliveryType, category, orderInfo, cartItems } = state
  const [pharmacyPartnerType, setPharmacyPartnerType] = useState<PharmacyPartnerType | undefined>(undefined)
  const [selectedOption, setSelectedOption] = useState<Option | undefined>()
  const [refillsQuantity, setRefillsQuantity] = useState<number | undefined>(undefined)
  const [pharmacy, setPharmacy] = useState<Pharmacy | undefined>(undefined)
  const [note, setNote] = useState<string | undefined>(undefined)

  const [productList, setProductList] = useState<ProductList[]>([])
  const [product, setProduct] = useState<Product | undefined>()
  const [loading, setLoading] = useState(false)
  const [isShowing, setIsShowing] = useState(false)
  const [showOrderFailed, setShowOrderFailed] = useState(false)
  const [showOrderCreated, setShowOrderCreated] = useState(false)
  const [showOrderCreatedTreat, setShowOrderCreatedTreat] = useState(false)
  const [error, setError] = useState('')
  const [noteError, setNoteError] = useState('')
  const [visitList, setVisitList] = useState<PastVisit[]>([])
  const [homeDeliveryPriceRange, setHomeDeliveryPriceRange] = useState<Range>({ From: 0, To: 0 })
  const [editCartItemId, setEditCartItemId] = useState<string>('')
  const [showDiscardOrdersWarning, setShowDiscardOrdersWarning] = useState<string>('')
  const resetRefillsQuantity = useCallback(() => {
    setRefillsQuantity(undefined)
  }, [])

  // Wrapper setters to preserve existing API while using reducer underneath
  const setDeliveryType = useCallback(
    (value: DeliveryType) => {
      dispatch({ type: 'SET_DELIVERY_TYPE', payload: { deliveryType: value } })
    },
    [dispatch],
  )

  const setCategory = useCallback(
    (value: OrderCategory | undefined) => {
      dispatch({ type: 'SET_CATEGORY', payload: { category: value } })
    },
    [dispatch],
  )

  const setOrderInfo = useCallback(
    (updater: OrderInfo | ((s: OrderInfo) => OrderInfo) | undefined) => {
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      const next = typeof updater === 'function' ? updater(orderInfo) : updater
      dispatch({ type: 'SET_ORDER_INFO', payload: { orderInfo: next } })
    },
    [dispatch, orderInfo],
  )

  const setCartItems = useCallback(
    (updater: CartItem[] | ((prev: CartItem[]) => CartItem[])) => {
      const next = typeof updater === 'function' ? updater(cartItems) : updater
      dispatch({ type: 'SET_CART_ITEMS', payload: { items: next } })
    },
    [dispatch, cartItems],
  )

  const { isMoudDeliveryAvailable, thriveProducts, standardProducts } = useIsMoudDeliveryAvailable({
    productList,
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    category,
  })

  const productsListForCurrentDeliveryType = useMemo(
    () =>
      productList
        .map((pL) => {
          const filteredProducts = pL.Products.filter((p) => {
            const isPharmacyPickup = deliveryType === 'pharmacy'
            return p.IsPharmacyPickupAllowed === isPharmacyPickup
          })
          // standardProducts/thriveProducts are only populated for MOUD home
          // delivery (Thrive vs. Honeybee split in MoudCombinedProductsView).
          // Skipping the substitution for other categories prevents MHT (which
          // also sets pharmacyPartnerType='honeybee' on home delivery) from
          // being replaced with an empty array.
          const isMoudCategory = category === 'moud'
          return {
            ...pL,
            Products:
              isMoudCategory && filteredProducts.length && pharmacyPartnerType === PharmacyPartners.honeybee.type ?
                standardProducts
              : isMoudCategory && filteredProducts.length && pharmacyPartnerType === PharmacyPartners.thrive.type ?
                thriveProducts
              : filteredProducts,
          }
        })
        .filter((pL) => pL.Products.length),
    [productList, pharmacyPartnerType, standardProducts, thriveProducts, deliveryType, category],
  )

  const isMoudHomeDelivery = category === 'moud' && deliveryType === 'home'
  const isMoudPharmacyPickup = category === 'moud' && deliveryType === 'pharmacy'
  const isMHTHomeDelivery = category === 'MenopauseHormoneTherapy' && deliveryType === 'home'
  const isMHTPharmacyPickup = category === 'MenopauseHormoneTherapy' && deliveryType === 'pharmacy'
  const isMHT = category === 'MenopauseHormoneTherapy'
  const isPharmacyPickup = isMHTPharmacyPickup || isMoudPharmacyPickup
  const isPharmacyHub =
    cartItems.some((item) => item.Order?.PharmacyPartner === PharmacyPartners.pharmacyHub.name) ||
    selectedOption?.PharmacyPartner === PharmacyPartners.pharmacyHub.name

  const stepData = useSteps({
    visitList,
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    isMoudDeliveryAvailable,
    visitId,
    category,
    setSelectedOption,
    resetRefillsQuantity,
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    setProduct,
    setOrderInfo,
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    orderInfo,
    isMoudHomeDelivery,
    cartItems,
    setShowDiscardOrdersWarning,
  })

  const { restart } = stepData

  useEffect(() => {
    if (!isShowing) restart()
  }, [isShowing, restart])

  const getCartSubTotal = useCallback(() => {
    let total = 0
    cartItems.forEach((item) => {
      // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
      total += item.Order.Price
    })
    return total
  }, [cartItems])
  const { getOrderNameFromProduct } = useGetOrderNameFromProduct({ isMoudHomeDelivery })

  const updateCart = useCallback(
    (currentOrderInfo?: OrderInfo, currentOption?: Option, currentProduct?: Product) => {
      const info = currentOrderInfo || orderInfo
      const option = currentOption || selectedOption
      const productItem = currentProduct || product
      if (editCartItemId) {
        setCartItems((prev) => {
          const position = prev.findIndex((cartItem) => cartItem.CartId === editCartItemId)
          prev[position] = {
            ...info,
            // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
            Order: {
              ...option,
              // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
              Price: isPharmacyPickup ? 0 : option.Price,
              Name: getOrderNameFromProduct(productItem),
            },
            RefillsQuantity: refillsQuantity ?? 0,
            DosageQuantity: info?.DosageQuantity,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            FulfillmentType: productItem.FulfillmentType,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            OptionId: option.OptionId,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            ProductId: productItem.ProductId,
            CartId: editCartItemId,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            Signature: info?.Signature ?? option.Signature ?? '',
            IsSubstitutionAllowed: true,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            DrugOptionId: info?.DrugOptionId ?? option.DrugOptionId ?? '',
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            DurationDays: info?.DurationDays ?? option.DurationDays ?? 0,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            DispenseAmount: info?.DispenseAmount ?? option.DurationDays ?? 0,
            EarliestFillDate: undefined,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            IsControlledSubstance: productItem.IsControlledSubstance,
          }
          return prev
        })
      } else {
        const item = {
          ...info,
          Order: {
            ...option,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            Price: isPharmacyPickup ? 0 : option.Price,
            Name: getOrderNameFromProduct(productItem),
          },
          RefillsQuantity: refillsQuantity ?? 0,
          DosageQuantity: info?.DosageQuantity,
          // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
          OptionId: option.OptionId,
          // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
          ProductId: productItem.ProductId,
          // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
          FulfillmentType: productItem.FulfillmentType,
          CartId: uuidv4(),
          // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
          Signature: option.Signature,
          IsSubstitutionAllowed: true,
          // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
          DrugOptionId: option.DrugOptionId || '',
          // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
          DurationDays: option.DurationDays,
          DispenseAmount: info?.DispenseAmount ?? 0,
          EarliestFillDate: undefined,
          // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
          IsControlledSubstance: productItem.IsControlledSubstance,
        }
        // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
        setCartItems((prev) => prev.concat(item))
        return item
      }
    },
    [
      editCartItemId,
      getOrderNameFromProduct,
      isPharmacyPickup,
      orderInfo,
      product,
      refillsQuantity,
      selectedOption,
      setCartItems,
    ],
  )

  // @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
  const updateCartRef = useAutoUpdatingRef(updateCart)

  const openProductsAction = useCallback(
    (args: {
      value: boolean
      categoryArg?: OrderCategory
      list?: ProductList[]
      range?: Range
      pharmacyFromProps?: Pharmacy
    }) => {
      const { value, categoryArg, range, pharmacyFromProps } = args
      setNote('')
      if (categoryArg) {
        setPharmacy(categoryArg === 'moud' ? pharmacyFromProps : undefined)
        setCategory(categoryArg)
        const delivery = getDeliveryType(categoryArg)
        if (delivery) setDeliveryType(delivery)
      }
      if (range) setHomeDeliveryPriceRange(range)
      setCartItems([])
      setPharmacyPartnerType(undefined)

      setOrderInfo(undefined)

      if (value) {
        // Reset every piece of per-product selection state, the same three-call set
        // `useSteps.goBack` and `CartView.onAddMoreScripts` perform when returning to
        // the product list. `refillsQuantity` in particular lives on this long-lived
        // provider, so leaving it set carries the previous order's refill count into
        // the next one and it is written straight into the cart item as
        // `RefillsQuantity`. Clearing `selectedOption` also matters on its own: the
        // confirm button on ProductSelectionView is disabled purely on
        // `selectedOption`, so a stale one leaves it enabled and clicking it calls
        // updateCart with a null product → crash reading productItem.ProductId.
        setSelectedOption(undefined)
        resetRefillsQuantity()
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        setProduct(null)
      }

      if (!visitId && !visitList.length) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        reduxDispatch(getPastVisitsForProduct(patientId))
        return
      }
      setIsShowing(value)
    },
    [
      setPharmacy,
      setCategory,
      setDeliveryType,
      setHomeDeliveryPriceRange,
      setCartItems,
      setOrderInfo,
      setProduct,
      setSelectedOption,
      resetRefillsQuantity,
      setNote,
      visitId,
      visitList,
      reduxDispatch,
      patientId,
      setIsShowing,
    ],
  )

  const getPriceRange = useCallback((products: Product[]) => {
    let From = 0
    let To = 0
    if (products.length) {
      products.forEach((item) => {
        From = From < item.PriceRange.From && From !== 0 ? From : item.PriceRange.From
        To = To > item.PriceRange.To ? To : item.PriceRange.To
      })
    }
    return { From, To }
  }, [])

  const value = useMemo(
    () => ({
      category,
      cartItems,
      deliveryType,
      isPatientRefillAllowed,
      note,
      orderInfo,
      patientId,
      pharmacy,
      refillsQuantity,
      editCartItemId,
      resetRefillsQuantity,
      selectedOption,
      setCategory,
      setCartItems,
      setEditCartItemId,
      setDeliveryType,
      setNote,
      setOrderInfo,
      setPharmacy,
      setSelectedOption,
      setRefillsQuantity,
      getCartSubTotal,
      productList: productsListForCurrentDeliveryType,
      product,
      loading,
      isShowing,
      showOrderFailed,
      showOrderCreated,
      showDiscardOrdersWarning,
      showOrderCreatedTreat,
      error,
      noteError,
      setProductList,
      setShowDiscardOrdersWarning,
      setProduct,
      setLoading,
      setIsShowing,
      setShowOrderFailed,
      setShowOrderCreated,
      setShowOrderCreatedTreat,
      setError,
      setNoteError,
      isMoudDeliveryAvailable,
      isPharmacyHub,
      previousCartStep,
      isMoudHomeDelivery,
      isMoudPharmacyPickup,
      isMHTHomeDelivery,
      isMHTPharmacyPickup,
      isPharmacyPickup,
      isMHT,
      visitList,
      setVisitList,
      homeDeliveryPriceRange,
      setHomeDeliveryPriceRange,
      openProductsAction,
      thriveProducts,
      standardProducts,
      getPriceRange,
      setPharmacyPartnerType,
      updateCart,
      pharmacyPartnerType,
      ...stepData,
    }),
    [
      category,
      cartItems,
      deliveryType,
      isPatientRefillAllowed,
      note,
      orderInfo,
      patientId,
      pharmacy,
      refillsQuantity,
      editCartItemId,
      resetRefillsQuantity,
      selectedOption,
      setCategory,
      setCartItems,
      setDeliveryType,
      setOrderInfo,
      getCartSubTotal,
      productsListForCurrentDeliveryType,
      product,
      loading,
      isShowing,
      showOrderFailed,
      showOrderCreated,
      showDiscardOrdersWarning,
      showOrderCreatedTreat,
      error,
      noteError,
      isMoudDeliveryAvailable,
      isMoudHomeDelivery,
      isMoudPharmacyPickup,
      isMHTHomeDelivery,
      isMHTPharmacyPickup,
      isPharmacyPickup,
      isMHT,
      visitList,
      homeDeliveryPriceRange,
      openProductsAction,
      getPriceRange,
      thriveProducts,
      standardProducts,
      setPharmacyPartnerType,
      updateCart,
      pharmacyPartnerType,
      stepData,
    ],
  )

  // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
  return <ProductOrderContext.Provider value={value}>{children}</ProductOrderContext.Provider>
}

const useProductOrderContext = () => {
  const context = useContext(ProductOrderContext)

  if (!context) {
    throw new Error('useProductOrderContext must be used within a ProductOrderProvider')
  }

  return context
}

export { ProductOrderProvider, useProductOrderContext, Step }
